import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Home, Factory, Building2 } from 'lucide-react';
import CTASection from '../components/CTASection';

interface Project {
  title: string;
  description: string;
  type: string;
  date: string;
  image: string;
  icon: typeof Home;
}

const ProjectsPage: React.FC = () => {
  const projects: Project[] = [
    {
      title: 'Complete elektrische installatie nieuwbouwwoning',
      description: 'Installatie van een moderne elektrische installatie in een nieuwbouwwoning, inclusief slimme verlichting, netwerkaansluitingen en een uitgebreid domotica systeem.',
      type: 'Particulier',
      date: 'December 2023',
      image: 'https://images.pexels.com/photos/3635548/pexels-photo-3635548.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: Home
    },
    {
      title: 'Renovatie bedrijfspand verlichting',
      description: 'Volledige renovatie van de verlichtingsinstallatie in een bedrijfspand, met focus op energiebesparing door LED-verlichting en bewegingssensoren.',
      type: 'Zakelijk',
      date: 'Oktober 2023',
      image: 'https://images.pexels.com/photos/257636/pexels-photo-257636.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: Factory
    },
    {
      title: 'Elektrische installatie kantoorpand',
      description: 'Installatie van een complete elektrische infrastructuur in een modern kantoorpand, inclusief datanetwerk en beveiligingssystemen.',
      type: 'Zakelijk',
      date: 'September 2023',
      image: 'https://images.pexels.com/photos/259957/pexels-photo-259957.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
      icon: Building2
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-12 bg-navy-900 text-white">
        <div className="container mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-bold mb-4 text-center"
          >
            Onze Projecten
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-xl text-center max-w-3xl mx-auto"
          >
            Een selectie van onze recent uitgevoerde werkzaamheden
          </motion.p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-lg overflow-hidden"
              >
                <div className="relative h-48">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-navy-800 text-white px-3 py-1 rounded-full text-sm">
                    {project.type}
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-navy-50 w-10 h-10 rounded-full flex items-center justify-center mr-3">
                      <project.icon size={20} className="text-navy-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-navy-800">{project.title}</h3>
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar size={14} className="mr-1" />
                        {project.date}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Project Types Section */}
          <div className="mt-16">
            <div className="text-center mb-12">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="text-3xl font-bold mb-4 text-navy-800"
              >
                Waar Wij Voor Staan
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="text-gray-600 max-w-2xl mx-auto"
              >
                Bij elk project streven we naar de hoogste kwaliteit en klanttevredenheid
              </motion.p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md text-center"
              >
                <div className="bg-navy-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Home size={32} className="text-navy-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-navy-800">Particulier</h3>
                <p className="text-gray-600">
                  Van kleine reparaties tot complete elektrische installaties voor nieuwbouw en renovatie
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white p-6 rounded-lg shadow-md text-center"
              >
                <div className="bg-navy-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Factory size={32} className="text-navy-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-navy-800">Zakelijk</h3>
                <p className="text-gray-600">
                  Professionele elektrische oplossingen voor bedrijfspanden en industriële toepassingen
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white p-6 rounded-lg shadow-md text-center"
              >
                <div className="bg-navy-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 size={32} className="text-navy-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-navy-800">Utiliteit</h3>
                <p className="text-gray-600">
                  Specialistische installaties voor kantoren, winkels en andere utiliteitsgebouwen
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default ProjectsPage;